import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, LogOut, LogIn, AlertTriangle } from "lucide-react";

const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

export function CleaningSchedule() {
  const [days, setDays] = useState("14");
  const [tasks, setTasks] = useState([]);

  const load = useCallback(() => api.get("/admin/cleaning", { params: { days: Number(days) } }).then(({ data }) => setTasks(data)), [days]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => { await api.patch(`/admin/cleaning/${id}`, body); load(); };

  const byDate = tasks.reduce((acc, t) => { (acc[t.date] ||= []).push(t); return acc; }, {});
  const todo = tasks.filter((t) => !t.done).length;
  const sameDay = tasks.filter((t) => t.same_day && !t.done).length;

  return (
    <div className="space-y-6" data-testid="cleaning-schedule">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-2xl text-olive flex items-center gap-2"><Sparkles className="w-5 h-5 text-rose-brand" /> Planning ménage</h3>
          <p className="text-sm text-muted-foreground"><span className="font-semibold text-olive" data-testid="cleaning-todo-count">{todo}</span> ménage(s) à faire{sameDay ? <> · <span className="text-rose-brand font-semibold">{sameDay} avec arrivée le même jour</span></> : ""}</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-36 bg-white" data-testid="cleaning-range-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="7">7 jours</SelectItem><SelectItem value="14">14 jours</SelectItem><SelectItem value="30">30 jours</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {tasks.length === 0 && <p className="text-sm text-muted-foreground" data-testid="cleaning-empty">Aucun départ sur la période.</p>}
      {Object.keys(byDate).sort().map((date) => (
        <div key={date}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{fmt(date)}</p>
          <div className="space-y-2">
            {byDate[date].map((t) => (
              <div key={t.booking_id} className={`rounded-2xl border p-4 flex flex-col md:flex-row md:items-center gap-3 ${t.done ? "bg-secondary/60 border-border opacity-70" : t.same_day ? "bg-rosebrand-pale/40 border-rose-brand/40" : "bg-white border-border"}`} data-testid={`cleaning-${t.booking_id}`}>
                <Checkbox checked={t.done} onCheckedChange={(v) => patch(t.booking_id, { done: !!v })} className="shrink-0" data-testid={`cleaning-done-${t.booking_id}`} />
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-olive ${t.done ? "line-through" : ""}`}>{t.listing_title}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><LogOut className="w-3.5 h-3.5" /> Départ {t.departing} · avant {t.checkout_time}</span>
                    {t.next_arrival ? (
                      <span className="flex items-center gap-1"><LogIn className="w-3.5 h-3.5" /> Arrivée {t.next_guest} ({t.next_guests} pers.) le {new Date(t.next_arrival + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                    ) : <span>Pas d'arrivée prévue ensuite</span>}
                  </div>
                </div>
                {t.same_day && !t.done && <Badge className="bg-rose-brand text-white shrink-0 flex items-center gap-1" data-testid={`cleaning-sameday-${t.booking_id}`}><AlertTriangle className="w-3 h-3" /> Même jour</Badge>}
                <Input defaultValue={t.assignee} placeholder="Qui s'en charge ?" onBlur={(e) => e.target.value !== t.assignee && patch(t.booking_id, { assignee: e.target.value })} className="h-9 md:w-40 bg-white" data-testid={`cleaning-assignee-${t.booking_id}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
